import React, { useState } from 'react';
import { callQueryLLM } from '../../../utils/apiClient'; // Adjust path as needed
import MarkdownRenderer from '../../../components/common/MarkdownRenderer';

// Assuming no specific AdminLayout or Card components are known, using basic structure.

const LlmQueryPage = () => {
  const [query, setQuery] = useState('');
  const [documentContext, setDocumentContext] = useState('');
  const [chatHistory, setChatHistory] = useState('');
  const [result, setResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setResult(null);

    // Attempt to parse documentContext and chatHistory if they are meant to be JSON
    let parsedDocumentContext = documentContext;
    if (documentContext.trim().startsWith('{') || documentContext.trim().startsWith('[')) {
        try {
            parsedDocumentContext = JSON.parse(documentContext);
        } catch (err) {
            // Not valid JSON, send as plain text
        }
    }

    let parsedChatHistory = chatHistory;
    if (chatHistory.trim().startsWith('{') || chatHistory.trim().startsWith('[')) {
        try {
            parsedChatHistory = JSON.parse(chatHistory);
        } catch (err) {
            // Not valid JSON, send as plain text
        }
    }

    try {
      // Pass the potentially parsed values or original strings to the API
      const data = await callQueryLLM(query, parsedDocumentContext, parsedChatHistory);
      setResult(data);
    } catch (err) {
      setError(err.message || 'An error occurred while querying the LLM.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="p-5">
      <h1>LLM Query Interface</h1>
      <div className="bg-blue-50 p-4 mb-4 border border-blue-200 rounded">
        <p><strong>Note:</strong> This feature requires the FastAPI server to be running on port 8001 with LLM integration configured.</p>
        <p className="mt-2">If you encounter an error, please check that the server is running and LLM integration is properly set up.</p>
      </div>
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label htmlFor="ai-query" className="block mb-1">Query:</label>
          <textarea
            id="ai-query"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            required
            rows={5}
            className="w-full p-2 border border-gray-300 rounded"
          />
        </div>
        <div className="mb-4">
          <label htmlFor="doc-context" className="block mb-1">Document Context (Optional):</label>
          <textarea
            id="doc-context"
            value={documentContext}
            onChange={(e) => setDocumentContext(e.target.value)}
            placeholder="Provide document context as JSON or plain text. For multiple documents, use an array of objects structure if sending JSON."
            rows={5}
            className="w-full p-2 border border-gray-300 rounded"
          />
        </div>
        <div className="mb-4">
          <label htmlFor="chat-history" className="block mb-1">Chat History (Optional):</label>
          <textarea
            id="chat-history"
            value={chatHistory}
            onChange={(e) => setChatHistory(e.target.value)}
            placeholder="Provide chat history as JSON (e.g., array of {role: 'user/model', parts: [{text: '...'}) or plain text."
            rows={5}
            className="w-full p-2 border border-gray-300 rounded"
          />
        </div>
        <button 
          type="submit" 
          disabled={isLoading} 
          style={{ 
            padding: '10px 15px', 
            border: 'none', 
            borderRadius: '4px', 
            backgroundColor: isLoading ? '#ccc' : '#007bff', 
            color: 'white', 
            cursor: 'pointer' 
          }}
        >
          {isLoading ? 'Querying...' : 'Query AI'}
        </button>
      </form>

      {error && (
        <div style={{ color: 'red', marginTop: '20px', padding: '10px', border: '1px solid red', borderRadius: '4px' }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {result && (
        <div className="mt-5 border border-gray-200 p-4 rounded bg-gray-50 dark:bg-gray-800 dark:border-gray-600">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">AI Response:</h2>
          {result.error && (
            <div className="mb-4 p-3 border border-yellow-300 rounded bg-yellow-50 dark:bg-yellow-900/20 dark:border-yellow-700">
              <strong className="text-yellow-800 dark:text-yellow-300">Note:</strong> 
              <span className="text-yellow-700 dark:text-yellow-300 ml-1">The AI service encountered an issue: {result.error}</span>
            </div>
          )}
          <div className="bg-white dark:bg-gray-900 p-4 border border-gray-200 dark:border-gray-700 rounded">
            <MarkdownRenderer 
              content={typeof result.response === 'object' ? JSON.stringify(result.response, null, 2) : result.response}
              className="llm-response-content"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default LlmQueryPage;
